import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowUpRight, Download } from "lucide-react";
import { Bar, CartesianGrid, ComposedChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { api, API } from "../lib/api";
import { COUNTY_SHAPES } from "../data/kenyaCounties";
import { Reveal, useSeo, cardUrl } from "../lib/ui";
import { ShareCard } from "../components/ShareCard";

const AXIS = { stroke: "#3f3f46", fontSize: 11, fontFamily: "JetBrains Mono, monospace" };
const usd = (n) => (Math.abs(n) >= 1e6 ? `$${(n / 1e6).toFixed(2)}m` : `$${Math.round(n || 0).toLocaleString()}`);
const int = (n) => Math.round(n || 0).toLocaleString();

const Stat = ({ label, value, note, testId }) => (
  <div className="bg-[#0b0b0b] p-5" data-testid={testId}>
    <p className="overline !text-zinc-500">{label}</p>
    <p className="mono mt-2 text-xl font-bold text-ochre md:text-2xl">{value}</p>
    {note ? <p className="mt-2 text-[0.62rem] uppercase tracking-[0.14em] text-zinc-400">{note}</p> : null}
  </div>
);

const Block = ({ eyebrow, title, children, testId }) => (
  <section className="mt-16 border-t border-zinc-900 pt-10" data-testid={testId}>
    <p className="eyebrow">{eyebrow}</p>
    <h2 className="display-3 mt-3">{title}</h2>
    {children}
  </section>
);

const ProjectTable = ({ rows, testId }) => (
  <div className="mt-6 overflow-x-auto border border-zinc-800" data-testid={testId}>
    <table className="w-full min-w-[760px] border-collapse text-left">
      <thead>
        <tr className="border-b border-zinc-800 bg-[#0b0b0b]">
          {["Project", "Units", "Step", "Build cost", "Running cost", "Trading income", "Jobs", "Players"].map((h) => (
            <th key={h} className="px-4 py-3 text-[0.65rem] font-bold uppercase tracking-[0.18em] text-zinc-300">{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((p, i) => (
          <tr key={p.item} className="border-b border-zinc-900" data-testid={`${testId}-row-${i}`}>
            <td className="px-4 py-3 text-sm text-white">{p.item}</td>
            <td className="mono px-4 py-3 text-sm text-zinc-300">{p.units}</td>
            <td className="mono px-4 py-3 text-sm text-maasai">Step {p.step}</td>
            <td className="mono px-4 py-3 text-sm text-zinc-300">{usd(p.capex_usd)}</td>
            <td className="mono px-4 py-3 text-sm text-zinc-300">{usd(p.opex_usd)}</td>
            <td className="mono px-4 py-3 text-sm text-zinc-300">{usd(p.annual_revenue_usd)}</td>
            <td className="mono px-4 py-3 text-sm text-zinc-300">{int(p.jobs)}</td>
            <td className="mono px-4 py-3 text-sm text-zinc-300">{int(p.players)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export default function CountyDossier() {
  const { slug } = useParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    setD(null);
    api.get(`/county-dossier/${slug}`).then((r) => setD(r.data)).catch(() => setErr("We could not load this county."));
  }, [slug]);

  useSeo({
    title: d ? `${d.county} County — KickStartKenyaFootball` : "County — KickStartKenyaFootball",
    description: d
      ? `${d.county} County: its people and output, the football projects proposed here, what it costs to produce a professional, what its players return, the build sequence, the health case and the value case.`
      : "County dossier",
    image: cardUrl("county", slug),
  });

  if (err)
    return (
      <div className="bg-black px-5 pb-24 pt-24 md:px-10" data-testid="dossier-error">
        <p className="border border-maasai/40 bg-maasai/10 p-6 text-sm text-white">{err}</p>
      </div>
    );
  if (!d) return <div className="min-h-[60vh] bg-black" data-testid="dossier-loading" />;

  const shape = COUNTY_SHAPES.find((s) => s.name === d.county);
  const m = d.money;
  const dev = d.development_cost;
  const t = d.trading;
  const h = d.health;
  const v = d.value_case;
  const chart = d.phasing.map((r) => ({
    year: r.year,
    build: +(-r.capex_usd / 1e6).toFixed(3),
    running: +(-r.opex_usd / 1e6).toFixed(3),
    facility: +(r.facility_income_usd / 1e6).toFixed(3),
    trading: +(r.trading_income_usd / 1e6).toFixed(3),
    cumulative: +(r.cumulative_usd / 1e6).toFixed(3),
  }));

  return (
    <div className="bg-black pb-24 pt-10 md:pt-14" data-testid="county-dossier">
      <section className="px-5 md:px-10">
        <div className="mx-auto max-w-[1500px]">
          <Reveal>
            <p className="eyebrow">County dossier</p>
            <div className="mt-4 flex flex-col gap-8 md:flex-row md:items-end md:justify-between">
              <div>
                <h1 className="display-1" data-testid="dossier-title">{d.county} County</h1>
                <p className="mono mt-4 flex flex-wrap items-center gap-3 text-[0.62rem] uppercase tracking-[0.16em] text-zinc-400">
                  {d.priority ? (
                    <span className="border border-ochre px-2 py-1 text-ochre" data-testid="dossier-priority">
                      Priority 18 · #{d.priority_rank}
                    </span>
                  ) : (
                    <span className="border border-white/20 px-2 py-1 text-zinc-400">Ranked #{d.priority_rank} of 47</span>
                  )}
                  <span>{int(d.census.population)} people in 2019</span>
                  <span className="text-zinc-400">/</span>
                  <span>{int(d.census.population_today)} today</span>
                  <span className="text-zinc-400">/</span>
                  <span>${d.economy.gdp_usd_bn}bn GDP · ${d.economy.gdp_ppp_usd_bn}bn PPP</span>
                </p>
              </div>
              {shape ? (
                <svg viewBox={`${shape.cx - 130} ${shape.cy - 130} 260 260`} className="h-28 w-28 shrink-0 md:h-36 md:w-36" aria-hidden="true">
                  <path d={shape.d} fill={d.priority ? "#E3211830" : "#ffffff10"} stroke="#E32118" strokeWidth="2" />
                </svg>
              ) : null}
            </div>
          </Reveal>

          <div className="mt-8 flex flex-wrap gap-3">
            <Link to={`/counties/${slug}/one-page`} className="btn-primary" data-testid="dossier-one-pager-link">
              {d.county} on one page <ArrowUpRight size={14} />
            </Link>
            <a href={`${API}/county-prospectus/${encodeURIComponent(d.county)}.pdf`} className="btn-primary" data-testid="dossier-pdf">
              <Download size={14} /> Download the business case
            </a>
            <Link to="/counties" className="btn-ghost" data-testid="dossier-map-link">
              County map <ArrowUpRight size={14} />
            </Link>
            <ShareCard
              title={`${d.county} County — KickStartKenyaFootball`}
              text={`${d.county} County: the costed plan for football here, county by county.`}
              card={cardUrl("county", slug)}
              label="Share this county"
              testId="county-share"
            />
            <Link to="/apply" className="btn-ghost" data-testid="dossier-proposal-link">
              Propose something here
            </Link>
            <Link to={`/counties/compare-sheet?a=${encodeURIComponent(d.county)}&b=Nairobi`} className="btn-ghost"
              data-testid="dossier-compare-sheet-link">
              One-page comparison sheet <ArrowUpRight size={14} />
            </Link>
            <Link to="/compare" className="btn-ghost" data-testid="dossier-compare-link">
              Compare with another county <ArrowUpRight size={14} />
            </Link>
            <Link to="/county-scenarios" className="btn-ghost" data-testid="dossier-shortlist-link">
              Add to county funding scenarios <ArrowUpRight size={14} />
            </Link>
          </div>

          {d.metro ? (
            <div className="mt-8 border-l-2 border-maasai bg-[#0b0b0b] p-6 md:p-8" data-testid="dossier-metro">
              <p className="overline text-ochre">{d.metro.label}</p>
              <div className="mt-5 grid gap-px bg-zinc-800 sm:grid-cols-2 lg:grid-cols-4">
                <Stat label="Metropolitan population today" value={int(d.metro.population_today)} note={`${int(d.metro.population_2019)} in the 2019 census`} testId="dossier-metro-total" />
                <Stat label="Times the city county alone" value={`${d.metro.multiple_of_core}x`} note={`${int(d.census.population_today)} in ${d.county} County`} testId="dossier-metro-multiple" />
                <Stat label="Share of all Kenyans" value={`${d.metro.share_of_national_pct}%`} note={`${int(d.metro.households)} households`} testId="dossier-metro-share" />
                <Stat label="Share of national output" value={`${d.metro.gdp_share_pct}%`} note={`${d.metro.city_gdp_share_pct}% from the city county alone`} testId="dossier-metro-gdp" />
              </div>
              <div className="mt-6 overflow-x-auto border border-zinc-800" data-testid="dossier-metro-table">
                <table className="w-full min-w-[520px] text-left text-sm">
                  <thead className="mono bg-[#111] text-[0.55rem] uppercase tracking-[0.14em] text-zinc-500">
                    <tr>
                      <th className="p-3">County</th>
                      <th className="p-3">2019 census</th>
                      <th className="p-3">Today</th>
                      <th className="p-3">Households</th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.metro.counties.map((c) => (
                      <tr key={c.county} className="border-b border-zinc-900" data-testid={`dossier-metro-row-${c.county}`}>
                        <td className={`p-3 ${c.core ? "text-ochre" : "text-white"}`}>
                          {c.county}
                          {c.core ? " · core" : ""}
                        </td>
                        <td className="p-3 text-zinc-400">{int(c.population_2019)}</td>
                        <td className="p-3 text-zinc-300">{int(c.population_today)}</td>
                        <td className="p-3 text-zinc-400">{int(c.households)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="mt-5 max-w-3xl text-sm leading-relaxed text-zinc-400">{d.metro.note}</p>
            </div>
          ) : null}

          {d.cost_of_living?.rent_ksh_month ? (
            <div className="mt-8 border-l-2 border-zinc-700 bg-[#0b0b0b] p-6 md:p-8" data-testid="dossier-cost-of-living">
              <p className="overline text-ochre">Cost of living here</p>
              <div className="mt-5 grid gap-px bg-zinc-800 sm:grid-cols-2 lg:grid-cols-3">
                <Stat label="Rent, mid-range two-bedroom" value={`KSh ${int(d.cost_of_living.rent_ksh_month)}`} note={`$${d.cost_of_living.rent_usd_month} a month`} testId="dossier-rent" />
                <Stat label="Cost-of-living index" value={d.cost_of_living.cost_index} note="1.00 is the national baseline" testId="dossier-col-index" />
                <Stat label="Cost to produce a professional" value={usd(d.development_cost?.cost_per_professional_usd || 0)} note={`${d.development_cost?.uk_multiple || "—"}x cheaper than England`} testId="dossier-col-cost-per-pro" />
              </div>
              <p className="mt-5 max-w-3xl text-sm leading-relaxed text-zinc-400">{d.cost_of_living.note}</p>
            </div>
          ) : null}

          <div className="mt-10 grid gap-px border border-zinc-800 bg-zinc-800 sm:grid-cols-2 lg:grid-cols-4" data-testid="dossier-headline">
            <Stat label="Units proposed here" value={int(d.projects.units)} note={`${d.projects.professional.length} professional, ${d.projects.grassroots.length} grassroots lines`} testId="dossier-units" />
            <Stat label="Build cost" value={usd(m.capex_usd)} note={`${usd(m.opex_usd)} a year to run`} testId="dossier-capex" />
            <Stat label="Trading income a year" value={usd(m.annual_revenue_usd)} note={`${m.cost_recovery_pct}% cost recovery`} testId="dossier-revenue" />
            <Stat label="Annual social and economic value" value={usd(m.annual_value_usd)} note={`$${m.value_per_dollar} per dollar built`} testId="dossier-value" />
            <Stat label="Players reached a year" value={int(m.players)} note={`${int(m.teams)} teams`} testId="dossier-players" />
            <Stat label="Permanent jobs" value={int(m.operational_jobs)} note={`${int(m.construction_job_years)} build person-years`} testId="dossier-jobs" />
            <Stat label="Coaching and referee places" value={int(m.coaching_places + m.referee_places)} note="Badged, paid and county-based" testId="dossier-training" />
            <Stat label="Tax back a year" value={usd(m.tax_usd)} note="VAT and PAYE" testId="dossier-tax" />
          </div>
          {d.profile.note ? <p className="mt-6 max-w-3xl text-sm leading-relaxed text-zinc-400">{d.profile.note}</p> : null}

          <Block eyebrow="Grassroots and recreational" title="What the community gets" testId="dossier-grassroots">
            <p className="mt-4 max-w-3xl text-sm leading-relaxed text-zinc-400">
              Courts, an upgraded 11-a-side ground and — where the population supports it — a covered arena.
              {" "}
              {usd(d.projects.grassroots_capex_usd)} of build cost, recreational hire income from day one, and the
              part of the programme that reaches the most people for the least money.
            </p>
            {d.projects.grassroots.length ? <ProjectTable rows={d.projects.grassroots} testId="dossier-grassroots-table" /> : null}
          </Block>

          <Block eyebrow="Professional" title="The professional team and its home" testId="dossier-professional">
            <p className="mt-4 max-w-3xl text-sm leading-relaxed text-zinc-400">
              Every county in this plan carries one professional pathway and one modular stadium —
              {" "}
              {usd(d.projects.professional_capex_usd)} of build cost here. Priority 18 counties get a
              10,000–15,000 seat venue; the rest get a 3,000–5,000 seat ground that can grow in stands rather than
              in concrete.
            </p>
            {d.projects.professional.length ? <ProjectTable rows={d.projects.professional} testId="dossier-professional-table" /> : null}
          </Block>

          {dev ? (
            <Block eyebrow="Cost per professional" title="What it costs to produce a player here" testId="dossier-cost">
              <div className="mt-6 grid gap-px border border-zinc-800 bg-zinc-800 sm:grid-cols-2 lg:grid-cols-4">
                <Stat label="Cost of living index" value={`x${dev.cost_index}`} note="Against the national baseline" testId="dossier-cost-index" />
                <Stat label="Per academy player a year" value={usd(dev.annual_per_player_usd)} note="Coaching, schooling, food, medical, kit" testId="dossier-cost-player" />
                <Stat label="Per professional produced" value={usd(dev.cost_per_professional_usd)} note={`${dev.uk_multiple}x cheaper than England`} testId="dossier-cost-pro" />
                <Stat label="Commercial ceiling" value={usd(dev.commercial_potential_usd)} note={`Funds ${dev.pros_funded_by_commercial} professionals a year`} testId="dossier-commercial" />
              </div>
            </Block>
          ) : null}

          {t ? (
            <Block eyebrow="Player trading" title="What its players return" testId="dossier-trading">
              <div className="mt-6 grid gap-px border border-zinc-800 bg-zinc-800 sm:grid-cols-2 lg:grid-cols-4">
                <Stat label="Academy places" value={int(t.academy_places)} note="Funded places on the pathway" testId="dossier-places" />
                <Stat label="Professionals a year" value={t.pros_per_year} note="At full delivery" testId="dossier-pros" />
                <Stat label="Trading income a year" value={usd(t.programme_income_usd)} note="Fees, sell-on and solidarity" testId="dossier-trading-income" />
                <Stat label="Value against production cost" value={`${t.return_multiple}x`} note="Per professional produced" testId="dossier-return" />
              </div>
            </Block>
          ) : null}

          <Block eyebrow="Recommended steps" title="The order it gets built here" testId="dossier-phasing">
            <div className="mt-6 h-[320px] w-full border border-zinc-800 bg-[#0b0b0b] p-4" data-testid="dossier-phasing-chart">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={chart} margin={{ top: 8, right: 12, bottom: 8, left: 0 }}>
                  <CartesianGrid stroke="#1c1c1c" />
                  <XAxis dataKey="year" tick={AXIS} tickFormatter={(y) => `Y${y}`} />
                  <YAxis tick={AXIS} width={56} tickFormatter={(x) => `$${x}m`} />
                  <Tooltip
                    contentStyle={{ background: "#000", border: "1px solid #3f3f46", fontSize: 12 }}
                    formatter={(x) => `$${(x * 1e6).toLocaleString()}`}
                  />
                  <Bar dataKey="build" name="Build" stackId="a" fill="#E32118" />
                  <Bar dataKey="running" name="Running" stackId="a" fill="#7c2d2a" />
                  <Bar dataKey="facility" name="Facility income" stackId="b" fill="#E5A91E" />
                  <Bar dataKey="trading" name="Trading income" stackId="b" fill="#5d8c3f" />
                  <Line dataKey="cumulative" name="Cumulative" stroke="#fff" strokeWidth={2} dot={false} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
            <p className="mt-4 max-w-3xl text-xs leading-relaxed text-zinc-400">
              This is the money view of the recommended steps for this county: the club base and service hubs first, then
              courts and pitches, then the academy, with the stadium last — by which point facility income and the
              first player sales are already arriving.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to={`/plans?county=${encodeURIComponent(d.county)}&track=professional`}
                className="btn-ghost"
                data-testid="dossier-steps-professional"
              >
                Recommended steps: the professional game
              </Link>
              <Link
                to={`/plans?county=${encodeURIComponent(d.county)}&track=national`}
                className="btn-ghost"
                data-testid="dossier-steps-national"
              >
                Recommended steps: the grassroots game
              </Link>
              <Link
                to={`/plans?county=${encodeURIComponent(d.county)}&track=harambee`}
                className="btn-ghost"
                data-testid="dossier-steps-harambee"
              >
                Recommended steps: Harambee Stars centre
              </Link>
              <a
                href={`${API}/steps-pack.pdf?track=professional&county=${encodeURIComponent(d.county)}`}
                className="btn-ghost"
                data-testid="dossier-steps-pack"
              >
                Steps pack (PDF)
              </a>
              <Link
                to={`/proposals?county=${encodeURIComponent(d.county)}`}
                className="btn-ghost"
                data-testid="dossier-proposal-prefilled"
              >
                Propose for {d.county}
              </Link>
            </div>
          </Block>

          <Block eyebrow="Health and fitness" title="Fewer people in hospital here" testId="dossier-health">
            <div className="mt-6 grid gap-px border border-zinc-800 bg-zinc-800 sm:grid-cols-2 lg:grid-cols-4">
              <Stat label="Players moving weekly" value={int(h.players)} note={`${int(h.active_minutes_per_year / 1000)}k active minutes a year`} testId="dossier-health-players" />
              <Stat label="Hospital admissions avoided" value={int(h.admissions_avoided)} note="NCD and respiratory, each year" testId="dossier-health-admissions" />
              <Stat label="Sick days recovered" value={int(h.sick_days_avoided)} note={`${usd(h.productivity_usd)} of output`} testId="dossier-health-sickdays" />
              <Stat label="Health and productivity value" value={usd(h.total_usd)} note={`$${h.per_player_usd} per player`} testId="dossier-health-total" />
            </div>
            <Link to="/health-and-fitness" className="btn-ghost mt-6" data-testid="dossier-health-link">
              The national health case <ArrowUpRight size={14} />
            </Link>
          </Block>

          {v ? (
            <Block eyebrow="The value case" title="Where this county's money already goes" testId="dossier-value-case">
              <div className="mt-6 grid gap-px border border-zinc-800 bg-zinc-800 sm:grid-cols-2 lg:grid-cols-4">
                <Stat label="Youth aged 15–24" value={int(v.youth_population)} note={`${v.secondary_enrolment ? int(v.secondary_enrolment) : "—"} in secondary school`} testId="dossier-youth" />
                <Stat label="Diaspora remittances" value={usd(v.remittances_usd)} note="Coming into this county a year" testId="dossier-remittances" />
                <Stat label="Spent on sports betting" value={`KSh ${int(v.betting_kes)}`} note="Money already inside football" testId="dossier-betting" />
                <Stat label="Share of national output" value={`${d.economy.share_of_gdp_pct}%`} note={`${d.census.share_of_population_pct}% of the population`} testId="dossier-share" />
              </div>
              <Link to="/value-case" className="btn-ghost mt-6" data-testid="dossier-value-link">
                The national value case <ArrowUpRight size={14} />
              </Link>
            </Block>
          ) : null}

          <p className="mt-12 max-w-3xl text-xs leading-relaxed text-zinc-500" data-testid="dossier-note">{d.note}</p>
        </div>
      </section>
    </div>
  );
}
